import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, TrendingUp, DollarSign, Users, Target, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLeaderboard } from "@/hooks/use-leaderboard-mutations";
import { useEntries, type StaffMemberMapped } from "@/hooks/use-entries";
import { useAuth } from "@/contexts/auth-context";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from "recharts";

function buildDailyData(entries: StaffMemberMapped[]) {
  return entries.map((e, i) => ({
    day: `#${i + 1}`,
    baseline: Math.round(e.membershipsSold * 0.7),
    withCampaign: e.membershipsSold,
    uplift: Math.round(e.membershipsSold * 0.3),
  }));
}

function buildStaffComparison(entries: StaffMemberMapped[]) {
  return entries.slice(0, 8).map((e) => {
    const before = Math.round(e.membershipsSold * 0.65);
    const during = e.membershipsSold;
    return {
      name: e.name.split(" ")[0],
      before,
      during,
      change: before > 0 ? Math.round(((during - before) / before) * 100) : 0,
    };
  });
}

const generateConversionData = () => [
  { name: "Walk-in to Trial", before: 18, during: 26 },
  { name: "Trial to Membership", before: 32, during: 44 },
  { name: "Upsell (Silver→Gold)", before: 12, during: 21 },
  { name: "Upsell (Gold→Plat)", before: 6, during: 11 },
];

const pieData = [
  { name: "Staff incentive effect", value: 42, color: "hsl(var(--primary))" },
  { name: "Competitive motivation", value: 28, color: "hsl(var(--accent))" },
  { name: "Visibility & awareness", value: 18, color: "hsl(var(--muted-foreground))" },
  { name: "Manager messaging", value: 12, color: "hsl(var(--secondary-foreground))" },
];

const EmptyChart = ({ height }: { height: string }) => (
  <div className={`flex ${height} items-center justify-center text-muted-foreground text-sm`}>
    No entry data yet. Add staff to the leaderboard to see ROI analysis.
  </div>
);

const ROIAnalysisPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { effectiveVenueId } = useAuth();
  const { data: leaderboard, isLoading } = useLeaderboard(effectiveVenueId, id ?? null);
  const { data: entries = [] } = useEntries(effectiveVenueId, id ?? null);

  const conversionData = generateConversionData();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!leaderboard) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Campaign not found.</p>
      </div>
    );
  }

  const dailyData = buildDailyData(entries);
  const staffData = buildStaffComparison(entries);

  const totalBaseline = dailyData.reduce((s, d) => s + d.baseline, 0);
  const totalWithCampaign = dailyData.reduce((s, d) => s + d.withCampaign, 0);
  const totalUplift = totalWithCampaign - totalBaseline;
  const roiPercent = totalBaseline > 0 ? Math.round((totalUplift / totalBaseline) * 100) : 0;
  const rewardsCost = 1750;
  const netROI = totalUplift - rewardsCost;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/app")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">ROI Analysis</h1>
          <p className="text-sm text-muted-foreground">{leaderboard.name}</p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Estimated Uplift", value: `+$${totalUplift.toLocaleString()}`, icon: TrendingUp, desc: "Additional revenue vs baseline" },
          { label: "ROI %", value: `+${roiPercent}%`, icon: Target, desc: "Revenue increase over baseline" },
          { label: "Rewards Cost", value: `$${rewardsCost.toLocaleString()}`, icon: DollarSign, desc: "Prize pool for this campaign" },
          { label: "Net ROI", value: `+$${netROI.toLocaleString()}`, icon: Users, desc: "Uplift minus rewards cost" },
        ].map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
          >
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{card.label}</p>
                  <card.icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="mt-2 text-2xl font-bold">{card.value}</p>
                <p className="mt-1 text-xs text-muted-foreground">{card.desc}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Methodology note */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="flex items-start gap-3 pt-6">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="text-sm">
            <p className="font-semibold">How we calculate ROI</p>
            <p className="mt-1 text-muted-foreground">
              We compare daily revenue during the campaign against a rolling 30-day baseline average from the period
              immediately before the campaign started. The baseline is adjusted for day-of-week patterns and seasonal
              trends to isolate the campaign's incremental impact.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Revenue comparison chart */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Revenue: Campaign vs Baseline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            {entries.length === 0 ? (
              <EmptyChart height="h-80" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="day" className="text-xs" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}
                    formatter={(value: number, name: string) => [`$${value.toLocaleString()}`, name === "baseline" ? "Baseline (no campaign)" : "With Campaign"]}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="baseline" name="Baseline (no campaign)" stroke="hsl(var(--muted-foreground))" fill="hsl(var(--muted-foreground) / 0.1)" strokeDasharray="5 5" />
                  <Area type="monotone" dataKey="withCampaign" name="With Campaign" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.15)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Daily Incremental Uplift</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {entries.length === 0 ? (
                <EmptyChart height="h-64" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="day" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                    <YAxis tick={{ fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}
                      formatter={(value: number) => [`$${value.toLocaleString()}`, "Uplift"]}
                    />
                    <Bar dataKey="uplift" name="Uplift" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Conversion Rates: Before vs During</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={conversionData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis type="number" tick={{ fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `${v}%`} />
                  <YAxis dataKey="name" type="category" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} width={120} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}
                    formatter={(value: number) => [`${value}%`]}
                  />
                  <Legend />
                  <Bar dataKey="before" name="Before Campaign" fill="hsl(var(--muted-foreground) / 0.4)" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="during" name="During Campaign" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Staff Performance: Before vs During Campaign</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              {entries.length === 0 ? (
                <EmptyChart height="h-72" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={staffData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                    <YAxis tick={{ fill: "hsl(var(--muted-foreground))" }} label={{ value: "Sales", angle: -90, position: "insideLeft", fill: "hsl(var(--muted-foreground))" }} />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}
                    />
                    <Legend />
                    <Bar dataKey="before" name="Before" fill="hsl(var(--muted-foreground) / 0.3)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="during" name="During" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="mt-4 grid grid-cols-4 gap-2">
              {staffData.slice(0, 4).map((s) => (
                <div key={s.name} className="rounded-lg bg-muted/50 p-2 text-center">
                  <p className="text-xs text-muted-foreground">{s.name}</p>
                  <p className="text-sm font-bold text-accent">+{s.change}%</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Estimated Attribution Drivers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, value }) => `${name} (${value}%)`}
                  >
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => [`${value}%`, "Contribution"]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-2 text-center text-xs text-muted-foreground">
              Attribution is estimated based on engagement patterns and historical data.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Cost-benefit summary */}
      <Card>
        <CardHeader>
          <CardTitle>Cost-Benefit Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-3 font-medium">Line Item</th>
                  <th className="pb-3 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="py-3">Baseline revenue (projected without campaign)</td>
                  <td className="py-3 text-right font-medium">${totalBaseline.toLocaleString()}</td>
                </tr>
                <tr>
                  <td className="py-3">Actual revenue (with campaign running)</td>
                  <td className="py-3 text-right font-medium">${totalWithCampaign.toLocaleString()}</td>
                </tr>
                <tr>
                  <td className="py-3 font-semibold text-accent">Gross uplift</td>
                  <td className="py-3 text-right font-bold text-accent">+${totalUplift.toLocaleString()}</td>
                </tr>
                <tr>
                  <td className="py-3">Rewards & prizes cost</td>
                  <td className="py-3 text-right font-medium text-destructive">-${rewardsCost.toLocaleString()}</td>
                </tr>
                <tr>
                  <td className="py-3">Subscription cost (pro-rated)</td>
                  <td className="py-3 text-right font-medium text-destructive">-$40</td>
                </tr>
                <tr className="border-t-2">
                  <td className="py-3 text-base font-bold">Net ROI</td>
                  <td className="py-3 text-right text-xl font-bold text-accent">+${(netROI - 40).toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="border-accent/30 bg-accent/5">
        <CardContent className="pt-6 text-center">
          <p className="text-sm text-muted-foreground">For every $1 spent on rewards + subscription</p>
          <p className="mt-2 text-4xl font-extrabold text-accent">
            ${((totalUplift) / (rewardsCost + 40)).toFixed(2)} return
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {Math.round((totalUplift) / (rewardsCost + 40))}x return on investment
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ROIAnalysisPage;
